import { useQuery } from '@tanstack/react-query';
import { Form } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Filter } from '../../../@types/filter';
import { confirmationApi } from '../../../apis';
import { EmployeeRelatedInformationDtoRolesEnum } from '../../../apis/client-axios';
import DatePickerWrap from '../../../components/DatePickerWrap';
import SvgApproved from '../../../components/svg/SvgApproved';
import SvgDeny from '../../../components/svg/SvgDeny';
import SvgPending from '../../../components/svg/SvgPending';
import TableWrap from '../../../components/TableWrap';
import Widget from '../../../components/Widget';
import { CommonStatus, DATE_FORMAT, YEAR_MONTH_FORMAT } from '../../../constants/common';
import { EMPLOYEE_APPLICATION } from '../../../constants/QueryKey';
import { RootState } from '../../../store';
import './style.scss';
import { statusMapping } from '../../../utils';
const EmployeeApplication = () => {
  const intl = useIntl();
  const [filter, setFilter] = useState<Filter>({ page: 1, pageSize: 20, fullTextSearch: '' });
  const employeeRole = useSelector((state: RootState) => state.auth.authUser?.role);
  const navigate = useNavigate();
  const { data: employeeApplicationData, isLoading } = useQuery({
    queryKey: [EMPLOYEE_APPLICATION, filter],
    queryFn: () =>
      confirmationApi.confirmationControllerGetListApprove(
        filter.date ? dayjs(filter.date).format(DATE_FORMAT) : dayjs().format(DATE_FORMAT),
        filter.date ? dayjs(filter.date).format(DATE_FORMAT) : dayjs().format(DATE_FORMAT),
        filter.page,
        undefined,
        undefined,
        true,
        filter.pageSize,
        undefined,
        filter.fullTextSearch,
      ),
    enabled: !!filter,
  });

  useEffect(() => {
    if (employeeRole && employeeRole !== EmployeeRelatedInformationDtoRolesEnum.Employee) {
      navigate(-1);
    }
  }, [navigate, employeeRole]);

  const columns = [
    {
      title: 'No.',
      dataIndex: 'stt',
      key: 'stt',
      render: (value, record, index) => {
        return <p className="employee-application_detail-table-column-data">{index + 1}</p>;
      },
    },
    {
      title: intl.formatMessage({ id: 'employee.application.column.apply' }),
      dataIndex: 'apply',
      key: 'apply',
      render: (_, record) => {
        return <p className="employee-application_detail-table-column-data">{record?.title}</p>;
      },
    },
    {
      title: intl.formatMessage({ id: 'application.approval.column.notes' }),
      dataIndex: 'notes',
      key: 'notes',
      render: (_, record) => {
        return <p className="employee-application_detail-table-column-data">{record?.notes}</p>;
      },
    },
    {
      title: intl.formatMessage({ id: 'employee.application.column.applyTime' }),
      dataIndex: 'applyTime',
      key: 'applyTime',
      render: (_, record) => {
        return <p className="employee-application_detail-table-column-data">{record?.timeRequest}</p>;
      },
    },
    {
      title: intl.formatMessage({ id: 'employee.application.column.status' }),
      dataIndex: 'status',
      key: 'status',
      render: (record) => {
        const { class: statusClass, intlText } = statusMapping[record] || statusMapping.default;
        const statusText = intlText ? intl.formatMessage({ id: intlText }) : '';
        return (
          <p className={`employee-application_detail-table-column-data-${statusClass}`}>
            {statusText}
            <div>
              {statusText === intl.formatMessage({ id: 'application.approval.button.deny' }) ? (
                <SvgDeny />
              ) : statusText === intl.formatMessage({ id: 'application.approval.approved' }) ? (
                <SvgApproved />
              ) : (
                <SvgPending />
              )}
            </div>
          </p>
        );
      },
    },
  ];
  return (
    <div className="employee-application">
      <Widget
        breadcrumbs={[intl.formatMessage({ id: 'employee.application.title' })]}
        content={
          <TableWrap
            isPagination
            loading={isLoading}
            onSearch={(search) => {
              setFilter((prevFilters) => ({
                ...prevFilters,
                page: 1,
                fullTextSearch: search,
              }));
            }}
            tableProps={{
              dataSource: Array.isArray(employeeApplicationData?.data?.data?.content)
                ? employeeApplicationData?.data?.data?.content
                : [],
              columns,
            }}
            paginationProps={{
              current: employeeApplicationData?.data?.data?.page || 1,
              pageSize: employeeApplicationData?.data?.data?.size || 20,
              total: employeeApplicationData?.data?.data?.total || 0,
              onChange: (page, pageSize) =>
                setFilter((prevFilters) => ({
                  ...prevFilters,
                  page,
                  pageSize,
                })),
            }}
            rightTopContent={
              <Form.Item name={'month'}>
                <DatePickerWrap
                  picker="month"
                  onChange={(date) => {
                    setFilter((prevFilters) => ({
                      ...prevFilters,
                      page: 1,
                      date,
                    }));
                  }}
                  format={YEAR_MONTH_FORMAT}
                  width={312}
                  defaultValue={dayjs()}
                  allowClear={false}
                />
              </Form.Item>
            }
          />
        }
        title={intl.formatMessage({ id: 'employee.application.title' })}
      />
    </div>
  );
};
export default EmployeeApplication;
